import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useRoute } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  GamepadIcon, 
  Users, 
  Clock, 
  BarChart, 
  ListChecks, 
  RefreshCw,
  Calculator
} from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminGameRoomPage() {
  const { user } = useAuth();
  const [, params] = useRoute('/admin/game-room/:id');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const gameId = params?.id ? parseInt(params.id) : -1;
  
  // Redirect if not admin
  if (!user || user.role !== 'admin') {
    setLocation('/');
    return null;
  }
  
  const { data: game, isLoading, refetch } = useQuery({
    queryKey: ['admin-game', gameId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/games/${gameId}`);
      if (!res.ok) throw new Error('Failed to fetch game');
      return await res.json();
    },
    enabled: gameId > 0,
  });
  
  const { data: rounds, isLoading: isLoadingRounds } = useQuery({
    queryKey: ['admin-game-rounds', gameId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/games/${gameId}/rounds`);
      if (!res.ok) throw new Error('Failed to fetch game rounds');
      return await res.json();
    },
    enabled: gameId > 0,
  });
  
  // Mutation for consolidating game points
  const consolidatePointsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/admin/games/${gameId}/consolidate-points`);
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: 'Points consolidated',
        description: 'Game points have been successfully consolidated',
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: ['admin-game', gameId] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error consolidating points',
        description: error.message || 'An error occurred while consolidating points',
        variant: 'destructive',
      });
    },
  });
  
  // Mutation for updating game rounds
  const updateGameRoundsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/admin/games/${gameId}/update-rounds`);
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: 'Game rounds updated',
        description: 'Game rounds have been successfully updated',
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: ['admin-game-rounds', gameId] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error updating game rounds',
        description: error.message || 'An error occurred while updating game rounds',
        variant: 'destructive',
      });
    },
  });
  
  const handleBackToAdmin = () => {
    setLocation('/admin');
  };
  
  const handleConsolidatePoints = () => {
    consolidatePointsMutation.mutate();
  };
  
  const handleUpdateGameRounds = () => {
    updateGameRoundsMutation.mutate();
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading game room details...</p>
        </div>
      </div>
    );
  }
  
  if (!game) {
    return (
      <div className="container mx-auto p-8">
        <div className="mb-6">
          <Button variant="outline" size="sm" onClick={handleBackToAdmin}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admin
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Game Room Not Found</CardTitle>
            <CardDescription>
              The requested game room could not be found or you don't have permission to view it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleBackToAdmin}>Return to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 pb-20">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleBackToAdmin}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admin
          </Button>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <GamepadIcon className="h-8 w-8" />
            Game Room Details
          </h1>
        </div>
        
        <div className="flex gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline"
                disabled={!game.isCompleted || game.isConsolidated}
                className={!game.isCompleted || game.isConsolidated ? "opacity-50" : ""}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Consolidate Points
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Consolidate Game Points</AlertDialogTitle>
                <AlertDialogDescription>
                  This action will consolidate all points from this game into players' profiles.
                  It can only be done once. Are you sure you want to continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConsolidatePoints}>
                  Consolidate
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Update Game Rounds
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Update Game Rounds</AlertDialogTitle>
                <AlertDialogDescription>
                  This will update and synchronize all game rounds for this game room.
                  Do you want to continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleUpdateGameRounds}>
                  Update
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      
      {/* Game Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Badge className={
                game.isCompleted ? "bg-green-100 text-green-800" : 
                game.isStarted ? "bg-blue-100 text-blue-800" : 
                "bg-yellow-100 text-yellow-800"
              }>
                {game.isCompleted ? "Completed" : game.isStarted ? "In Progress" : "Pending"}
              </Badge>
              {game.isConsolidated && (
                <Badge className="ml-2 bg-purple-100 text-purple-800">
                  Points Consolidated
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Game Type</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="outline">
              {game.gameType}
            </Badge>
            <Badge variant="outline" className="ml-2">
              {game.spectatorMode} spectators
            </Badge>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Created</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center">
            <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
            <span>{new Date(game.createdAt).toLocaleString()}</span>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Rounds</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{game.numberOfRounds}</div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Game Details</TabsTrigger>
          <TabsTrigger value="rounds">Game Rounds</TabsTrigger>
          <TabsTrigger value="players">Players</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Game Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Basic Information</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">ID:</span> {game.id}
                    </div>
                    <div>
                      <span className="font-medium">Name:</span> {game.name}
                    </div>
                    <div>
                      <span className="font-medium">Created by:</span> User ID {game.creator}
                    </div>
                    <div>
                      <span className="font-medium">Started:</span> {game.startedAt ? new Date(game.startedAt).toLocaleString() : 'Not started'}
                    </div>
                    <div>
                      <span className="font-medium">Completed:</span> {game.completedAt ? new Date(game.completedAt).toLocaleString() : 'Not completed'}
                    </div>
                    <div>
                      <span className="font-medium">Last Activity:</span> {game.lastActivity ? new Date(game.lastActivity).toLocaleString() : 'Unknown'}
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Game Results</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Winner:</span> {game.winner ? `User ID ${game.winner}` : 'No winner yet'}
                    </div>
                    <div>
                      <span className="font-medium">Last Round Winner:</span> {game.lastRoundWinner ? `User ID ${game.lastRoundWinner}` : 'None'}
                    </div>
                    <div>
                      <span className="font-medium">Player 1 Score:</span> {game.player1Score}
                    </div>
                    <div>
                      <span className="font-medium">Player 2 Score:</span> {game.player2Score}
                    </div>
                    <div>
                      <span className="font-medium">End Reason:</span> {game.endReason || 'Not ended'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {game.tournamentId && (
            <Card>
              <CardHeader>
                <CardTitle>Tournament Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Tournament ID:</span> {game.tournamentId}
                  </div>
                  <div>
                    <span className="font-medium">Match ID:</span> {game.matchId || 'Not assigned'}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => game.tournamentId && setLocation(`/admin/tournament/${game.tournamentId}`)}
                  >
                    View Tournament
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="rounds" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Game Rounds</CardTitle>
              <CardDescription>
                {isLoadingRounds ? 'Loading rounds...' : 
                  rounds && rounds.length > 0 ? `${rounds.length} rounds found` : 'No rounds found'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingRounds ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : !rounds || rounds.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-muted/50">
                  No game rounds found for this game
                </div>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">ID</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Current?</th>
                        <th className="text-left p-2">Winner</th>
                        <th className="text-left p-2">Player 1 Score</th>
                        <th className="text-left p-2">Player 2 Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rounds.map((round: any) => (
                        <tr key={round.id} className="border-t hover:bg-muted/50">
                          <td className="p-2">{round.id}</td>
                          <td className="p-2">
                            <Badge className={round.isCompleted ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}>
                              {round.isCompleted ? "Completed" : "In Progress"}
                            </Badge>
                          </td>
                          <td className="p-2">
                            {round.isCurrentRound && <Badge variant="outline">Current</Badge>}
                          </td>
                          <td className="p-2">{round.winner ? `User ID ${round.winner}` : 'No winner'}</td>
                          <td className="p-2">{round.player1Score}</td>
                          <td className="p-2">{round.player2Score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="players" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Players</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    Player Information
                  </h3>
                  <div className="space-y-3">
                    <div className="p-3 rounded-md bg-muted/50">
                      <div className="font-medium">Player 1</div>
                      <div>User ID: {game.player1 || 'Not assigned'}</div>
                      <div>Score: {game.player1Score}</div>
                    </div>
                    <div className="p-3 rounded-md bg-muted/50">
                      <div className="font-medium">Player 2</div>
                      <div>User ID: {game.player2 || 'Not assigned'}</div>
                      <div>Score: {game.player2Score}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <BarChart className="h-4 w-4 mr-2" />
                    Game Statistics
                  </h3>
                  <div className="space-y-3">
                    <div className="p-3 rounded-md bg-muted/50">
                      <div className="font-medium">Score Summary</div>
                      <div>Total Rounds: {game.numberOfRounds}</div>
                      <div>Rounds Completed: {rounds?.filter((r: any) => r.isCompleted).length || 0}</div>
                      <div>Current Winner: {game.player1Score > game.player2Score ? 'Player 1' : game.player2Score > game.player1Score ? 'Player 2' : 'Tie'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}